import { Component } from '@theme/component';
import { trapFocus, removeTrapFocus } from '@theme/focus';
import { onAnimationEnd, removeWillChangeOnAnimationEnd } from '@theme/utilities';

/**
 * A custom element that manages the main menu drawer.
 *
 * @typedef {object} Refs
 * @property {HTMLDetailsElement} details - The details element.
 * @property {HTMLDivElement} menuDrawer - The slideable drawer panel containing the menu.
 *
 * @extends {Component<Refs>}
 */
class HeaderDrawer extends Component {
  requiredRefs = ['details', 'menuDrawer'];

  connectedCallback() {
    super.connectedCallback();

    this.addEventListener('keyup', this.#onKeyUp);
    this.#setupAnimatedElementListeners();
    // The header's "menu is open" state as a class (header.liquid: `.header.r69-menu-open`), mirrored
    // from the native `toggle` event so it is right however `open` changes — a summary tap, our
    // open()/close(), or reset(). It used to be `.header:has(#Details-menu-drawer-container[open])`,
    // and a `[open]` inside :has() makes Blink re-walk the anchor's subtree (the 3 000-element
    // header) on EVERY `open` attribute change anywhere under it (collection page, CPU ×4,
    // 06.09.2026: the three `[open]`-in-:has() rules were 130 of the 200 ms a drawer close cost).
    this.refs.details.addEventListener('toggle', this.#mirrorMenuOpen);
    this.#mirrorMenuOpen();
    // ROTAS69: the top level of the navigation (~65 elements) enters the DOM in idle time after load,
    // so the tap on the burger only shows it. Inserted on the tap itself, it cost the first frame
    // ~40 ms on a 4x-slowed phone (01.11.2026). The submenus follow once the drawer has opened.
    const whenIdle = window.requestIdleCallback || ((callback) => setTimeout(callback, 1500));
    whenIdle(() => this.#hydrateNavigation(), { timeout: 4000 });
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.removeEventListener('keyup', this.#onKeyUp);
    this.refs.details.removeEventListener('toggle', this.#mirrorMenuOpen);
  }

  #mirrorMenuOpen = () => {
    // A safety net for any way of opening that bypasses open(): the navigation must be there.
    if (this.refs.details.open) this.#hydrateNavigation();
    const header = this.closest('.header') || document.getElementById('header-component');
    header?.classList.toggle('r69-menu-open', this.refs.details.open);
  };

  /**
   * ROTAS69: the drawer's navigation ships inside <template data-r69-drawer-nav>
   * (snippets/header-drawer.liquid) and enters the DOM here — in idle time after load, or right
   * before the first open if that comes sooner. Its submenus (~680 elements) stay in their own
   * templates until the drawer opens, so a closed menu is not part of the page, nor of any session
   * recording.
   */
  #hydrateNavigation() {
    const template = this.querySelector('template[data-r69-drawer-nav]');
    if (!(template instanceof HTMLTemplateElement)) return;

    template.replaceWith(template.content);
    this.#setupAnimatedElementListeners();
  }

  /**
   * ROTAS69: a submenu's own content ships in <template data-r69-submenu> inside the navigation
   * template and enters the DOM right before that submenu opens. Opening the drawer then inserts
   * only the top level (~65 elements): inserting all ~750 at once slowed the burger's tap-to-paint
   * from 104 to 152 ms (median of 5, phone with a 4x-slowed CPU, 01.11.2026).
   * @param {HTMLDetailsElement} details - The submenu being opened
   */
  #hydrateSubmenu(details) {
    const template = details.querySelector(':scope > .menu-drawer__submenu > template[data-r69-submenu]');
    if (!(template instanceof HTMLTemplateElement)) return;

    template.replaceWith(template.content);
    this.#setupAnimatedElementListeners();
  }

  /**
   * ROTAS69: all submenus at once, when the drawer has finished opening — before anyone taps one,
   * so that tap only shows it, and before the focus trap collects the drawer's focusable elements.
   */
  #hydrateSubmenus() {
    const templates = this.querySelectorAll('template[data-r69-submenu]');
    if (!templates.length) return;

    templates.forEach((template) => template.replaceWith(template.content));
    this.#setupAnimatedElementListeners();
  }

  /**
   * Close the main menu drawer when the Escape key is pressed
   * @param {KeyboardEvent} event
   */
  #onKeyUp = (event) => {
    if (event.key !== 'Escape') return;

    this.#close(this.#getDetailsElement(event));
  };

  /**
   * @returns {boolean} Whether the main menu drawer is open
   */
  get isOpen() {
    return this.refs.details.hasAttribute('open');
  }

  /**
   * Get the closest details element to the event target
   * @param {Event | undefined} event
   * @returns {HTMLDetailsElement}
   */
  #getDetailsElement(event) {
    if (!(event?.target instanceof Element)) return this.refs.details;

    return event.target.closest('details') ?? this.refs.details;
  }

  /**
   * Toggle the main menu drawer
   */
  toggle() {
    return this.isOpen ? this.close() : this.open();
  }

  /**
   * Open the closest drawer or the main menu drawer
   * @param {string} [target]
   * @param {Event} [event]
   */
  open(target, event) {
    this.#hydrateNavigation();
    const details = this.#getDetailsElement(event);
    if (details !== this.refs.details) this.#hydrateSubmenu(details);
    const summary = details.querySelector('summary');

    if (!summary) return;

    summary.setAttribute('aria-expanded', 'true');

    this.preventInitialAccordionAnimations(details);
    requestAnimationFrame(() => {
      details.classList.add('menu-open');

      if (target) {
        this.refs.menuDrawer.classList.add('menu-drawer--has-submenu-opened');
      }

      // Wait for the drawer animation to complete before trapping focus
      const drawer = details.querySelector('.menu-drawer, .menu-drawer__submenu');
      onAnimationEnd(
        drawer || details,
        () => {
          if (details === this.refs.details) this.#hydrateSubmenus();
          trapFocus(details);
        },
        { subtree: false }
      );
    });
  }

  /**
   * Go back or close the main menu drawer
   * @param {Event} [event]
   */
  back(event) {
    this.#close(this.#getDetailsElement(event));
  }

  /**
   * Close the main menu drawer
   */
  close() {
    this.#close(this.refs.details);
  }

  /**
   * Close the closest menu or submenu that is open
   *
   * @param {HTMLDetailsElement} details
   */
  #close(details) {
    const summary = details.querySelector('summary');

    if (!summary) return;

    summary.setAttribute('aria-expanded', 'false');
    details.classList.remove('menu-open');
    this.refs.menuDrawer.classList.remove('menu-drawer--has-submenu-opened');

    // Wait for the .menu-drawer element's transition, not the entire details subtree
    // This avoids waiting for child accordion/resource-card animations which can cause issues on Firefox
    const drawer = details.querySelector('.menu-drawer, .menu-drawer__submenu');

    onAnimationEnd(
      drawer || details,
      () => {
        reset(details);
        if (details === this.refs.details) {
          removeTrapFocus();
          const openDetails = this.querySelectorAll('details[open]:not(accordion-custom > details)');
          openDetails.forEach(reset);
        } else {
          trapFocus(this.refs.details);
        }
      },
      { subtree: false }
    );
  }

  /**
   * Attach animationend event listeners to all animated elements to remove will-change after animation
   * to remove the stacking context and allow submenus to be positioned correctly
   */
  #setupAnimatedElementListeners() {
    const allAnimated = this.querySelectorAll('.menu-drawer__animated-element');
    allAnimated.forEach((element) => {
      element.addEventListener('animationend', removeWillChangeOnAnimationEnd);
    });
  }

  /**
   * Temporarily disables accordion animations to prevent unwanted transitions when the drawer opens.
   * Adds a no-animation class to accordion content elements, then removes it after 100ms to
   * re-enable animations for user interactions.
   * @param {HTMLDetailsElement} details - The details element containing the accordions
   */
  preventInitialAccordionAnimations(details) {
    const content = details.querySelectorAll('accordion-custom .details-content');

    content.forEach((element) => {
      if (element instanceof HTMLElement) {
        element.classList.add('details-content--no-animation');
      }
    });
    setTimeout(() => {
      content.forEach((element) => {
        if (element instanceof HTMLElement) {
          element.classList.remove('details-content--no-animation');
        }
      });
    }, 100);
  }
}

if (!customElements.get('header-drawer')) {
  customElements.define('header-drawer', HeaderDrawer);
}

/**
 * Reset an open details element to its original state
 *
 * @param {HTMLDetailsElement} element
 */
function reset(element) {
  element.classList.remove('menu-open');
  element.removeAttribute('open');
  element.querySelector('summary')?.setAttribute('aria-expanded', 'false');
}
